import React, { useEffect, useState } from "react";
import Logo from "./Logo";

const LINKS = [
  { id: "about", label: "About" },
  { id: "capabilities", label: "Capabilities" },
  { id: "bandera", label: "Bandera Oil Tools" },
  { id: "team", label: "Team" },
  { id: "contact", label: "Contact" },
];

export default function NavMenu() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");

  // Swap the bar from transparent to solid once the hero starts leaving
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scroll-spy: highlight the link for whichever section owns the viewport
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)
          .slice(0, 1)
          .forEach((e) => setActive(e.target.id));
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
    );
    LINKS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  // Lock the page behind the mobile drawer, and allow Esc to close it
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const go = (e, id) => {
    e.preventDefault();
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      {/* Utility strip — address and direct lines */}
      <div
        className={`hidden border-b border-white/10 bg-ink-950/80 backdrop-blur md:block
                    transition-all duration-500 ${
                      scrolled
                        ? "-translate-y-full opacity-0"
                        : "translate-y-0 opacity-100"
                    }`}
      >
        <div className="mx-auto flex max-w-shell items-center justify-between px-6 py-2 font-mono text-[11px] tracking-wide2 text-steel-400 lg:px-10">
          <span className="uppercase">
            Lot #2 Otaheite Industrial Park · South Oropouche, Trinidad
          </span>
          <span className="flex items-center gap-6">
            <a
              href="tel:8684748903"
              className="transition-colors hover:text-flame-500"
            >
              868 474 8903
            </a>
            <a
              href="tel:8682721480"
              className="transition-colors hover:text-flame-500"
            >
              868 272 1480
            </a>
            <a
              href="mailto:info@anfieldtt.com"
              className="transition-colors hover:text-flame-500"
            >
              info@anfieldtt.com
            </a>
          </span>
        </div>
      </div>

      {/* Main bar */}
      <nav
        className={`transition-all duration-500 ${
          scrolled || open
            ? "border-b border-white/10 bg-ink-950/90 backdrop-blur-xl"
            : "border-b border-transparent bg-gradient-to-b from-ink-950/70 to-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-shell items-center justify-between px-6 lg:px-10">
          <a
            href="#top"
            onClick={(e) => {
              e.preventDefault();
              setOpen(false);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex shrink-0 items-center py-4"
            aria-label="Anfield Services Limited — home"
          >
            <Logo height={scrolled ? 34 : 44} />
          </a>

          {/* Desktop links */}
          <div className="hidden items-center gap-1 lg:flex">
            {LINKS.map((link) => (
              <a
                key={link.id}
                href={`#${link.id}`}
                onClick={(e) => go(e, link.id)}
                className={`group relative px-4 py-6 font-mono text-[11px] uppercase tracking-wide2
                            transition-colors duration-300 ${
                              active === link.id
                                ? "text-white"
                                : "text-steel-400 hover:text-white"
                            }`}
              >
                {link.label}
                <span
                  className={`absolute inset-x-4 bottom-4 h-px origin-left bg-flame-500
                              transition-transform duration-300 ${
                                active === link.id
                                  ? "scale-x-100"
                                  : "scale-x-0 group-hover:scale-x-100"
                              }`}
                />
              </a>
            ))}
            <a
              href="#contact"
              onClick={(e) => go(e, "contact")}
              className="ml-4 bg-flame-500 px-6 py-3 font-mono text-[11px] uppercase tracking-wide2 text-white transition-colors duration-300 hover:bg-flame-600"
            >
              Request a Quote
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="relative z-50 -mr-2 flex h-11 w-11 flex-col items-center justify-center gap-[5px] lg:hidden"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            <span
              className={`block h-[2px] w-6 bg-white transition-transform duration-300 ${
                open ? "translate-y-[7px] rotate-45" : ""
              }`}
            />
            <span
              className={`block h-[2px] w-6 bg-white transition-opacity duration-300 ${
                open ? "opacity-0" : ""
              }`}
            />
            <span
              className={`block h-[2px] w-6 bg-white transition-transform duration-300 ${
                open ? "-translate-y-[7px] -rotate-45" : ""
              }`}
            />
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 top-0 z-40 bg-ink-950 transition-all duration-500 lg:hidden ${
          open
            ? "pointer-events-auto opacity-100"
            : "pointer-events-none opacity-0"
        }`}
      >
        <div className="absolute inset-0 bg-grid bg-grid opacity-40" />
        <div className="relative flex h-full flex-col justify-center px-8">
          {LINKS.map((link, i) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              onClick={(e) => go(e, link.id)}
              className="group flex items-baseline gap-4 border-b border-white/10 py-5"
              style={{
                transitionDelay: `${open ? 120 + i * 60 : 0}ms`,
                transform: open ? "none" : "translateY(16px)",
                opacity: open ? 1 : 0,
                transition: "transform .5s cubic-bezier(.16,1,.3,1), opacity .5s",
              }}
            >
              <span className="font-mono text-[11px] text-flame-500">
                0{i + 1}
              </span>
              <span className="h-display text-3xl transition-colors group-hover:text-flame-500">
                {link.label}
              </span>
            </a>
          ))}
          <div className="mt-10 space-y-2 font-mono text-xs text-steel-400">
            <a href="tel:8684748903" className="block hover:text-flame-500">
              868 474 8903
            </a>
            <a href="tel:8682721480" className="block hover:text-flame-500">
              868 272 1480
            </a>
            <a
              href="mailto:info@anfieldtt.com"
              className="block hover:text-flame-500"
            >
              info@anfieldtt.com
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
